import { Roles_Enum } from "lib/graphql-generated";
import { useUser } from "lib/hooks/use-user";
import Link from "next/link";
import { ReactNode, useState } from "react";
import { LogoIcon } from "../svg/logo-icon";
import { Transition } from "@headlessui/react";
import { useRouter } from "next/router";
import { AdminLink } from "./admin-link";
import Head from "next/head";
import { AdminSearchBar } from "./admin-search-bar";

interface Props {
  page: string;
  showingModal?: boolean;
  children?: any;
  searchBar?: ReactNode;
}

export const AdminLayout = ({
  page,
  children,
  showingModal,
  searchBar,
}: Props) => {
  const { route } = useRouter();
  const { userData } = useUser(Roles_Enum.Bookseller, {
    redirectTo: "/signin",
  });
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  return (
    <>
      <Head>
        <title>LH Admin | {page}</title>
      </Head>
      <div
        className={`${
          menuOpen ? "overflow-y-hidden" : ""
        } relative  min-h-screen flex`}>
        {/* <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. --> */}
        <Transition show={menuOpen}>
          {(ref) => (
            <nav ref={ref} className='md:hidden'>
              <div className=' fixed inset-0 z-40 flex'>
                <Transition.Child
                  enter='transition-opacity ease-linear duration-300'
                  enterFrom='opacity-0'
                  enterTo='opacity-100'
                  leave='transition-opacity ease-linear duration-300'
                  leaveFrom='opacity-100'
                  leaveTo='opacity-0'>
                  <div className='fixed inset-0' aria-hidden='true'>
                    <div className='absolute inset-0 bg-gray-600 opacity-75'></div>
                  </div>
                </Transition.Child>
                <Transition.Child
                  enter='transition ease-in-out duration-300 transform'
                  enterFrom='-translate-x-full'
                  enterTo='translate-x-0'
                  leave='transition ease-in-out duration-300 transform'
                  leaveFrom='translate-x-0'
                  leaveTo='-translate-x-full'>
                  <div className='relative h-full max-w-xs w-full bg-white pt-5 pb-4 flex-1 flex flex-col'>
                    <div className='absolute top-0 right-0 -mr-14 p-1'>
                      <button
                        onClick={() => setMenuOpen(false)}
                        className='h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:bg-gray-600'>
                        {/* <!-- Heroicon name: x --> */}
                        <svg
                          className='h-6 w-6 text-white'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                          aria-hidden='true'>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M6 18L18 6M6 6l12 12'
                          />
                        </svg>
                        <span className='sr-only'>Close sidebar</span>
                      </button>
                    </div>
                    <div className='flex-shrink-0 px-4 '>
                      <Link href='/'>
                        <a className='flex items-center'>
                          <LogoIcon className='h-12 mx-auto' />
                        </a>
                      </Link>
                    </div>
                    <div className='mt-5 flex-1 h-0 overflow-y-auto'>
                      <nav className='h-full flex flex-col'>
                        <div className='space-y-1'>
                          <AdminLink
                            href='/admin'
                            selected={
                              route.split("/")[1] === "admin" &&
                              route.split("/").length < 3
                            }>
                            <svg
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                              aria-hidden='true'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
                              />
                            </svg>
                            Home
                          </AdminLink>

                          <AdminLink
                            href='/admin/posts'
                            selected={route.includes("/posts")}>
                            <svg
                              aria-hidden='true'
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                              />
                            </svg>
                            Posts
                          </AdminLink>

                          <AdminLink
                            href='/admin/orders'
                            selected={route.includes("/orders")}>
                            <svg
                              aria-hidden='true'
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z'
                              />
                            </svg>
                            Orders
                          </AdminLink>

                          <AdminLink
                            href='/admin/books'
                            selected={route.includes("/books")}>
                            <svg
                              aria-hidden='true'
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                              />
                            </svg>
                            Books
                          </AdminLink>

                          <AdminLink
                            href='/admin/categories'
                            selected={route.includes("/categories")}>
                            <svg
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z'
                              />
                            </svg>
                            Categories
                          </AdminLink>

                          <AdminLink
                            href='/admin/tags'
                            selected={route.includes("/tags")}>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z'
                              />
                            </svg>
                            Tags
                          </AdminLink>

                          <AdminLink
                            href='/admin/products'
                            selected={route.includes("/products")}>
                            <svg
                              aria-hidden='true'
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z'
                              />
                            </svg>
                            Products
                          </AdminLink>

                          <AdminLink
                            href='/admin/subscriptions'
                            selected={route.includes("/subscriptions")}>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7'
                              />
                            </svg>
                            Subscriptions
                          </AdminLink>

                          <AdminLink
                            href='/admin/vouchers'
                            selected={route.includes("/vouchers")}>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z'
                              />
                            </svg>
                            Vouchers
                          </AdminLink>

                          <AdminLink
                            href='/admin/discounts'
                            selected={route.includes("/discounts")}>
                            <svg
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z'
                              />
                            </svg>
                            Discounts
                          </AdminLink>

                          <AdminLink
                            href='/admin/events'
                            selected={route.includes("/events")}>
                            <svg
                              aria-hidden='true'
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                              />
                            </svg>
                            Events & Festivals
                          </AdminLink>
                          <AdminLink
                            href='/admin/pages'
                            selected={route.includes("/pages")}>
                            <svg
                              className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2'
                              />
                            </svg>
                            Pages
                          </AdminLink>
                          <AdminLink
                            href='/admin/settings'
                            selected={route.includes("/settings")}>
                            <svg
                              aria-hidden='true'
                              className='mr-3 h-6 w-6'
                              xmlns='http://www.w3.org/2000/svg'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'>
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
                              />
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                              />
                            </svg>
                            Settings
                          </AdminLink>
                          {/* <!-- Current: "bg-purple-50 border-purple-600 text-purple-600", Default: "text-gray-600 hover:bg-gray-50 hover:text-gray-900" --> */}
                        </div>
                        <div className='mt-auto pt-10 space-y-1'>
                          <Link href='/logout'>
                            <a className='group rounded-md py-2 px-4 flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50'>
                              {/* <!-- Heroicon name: cog --> */}
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                                strokeWidth={2}>
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
                                />
                              </svg>
                              Logout
                            </a>
                          </Link>
                        </div>
                      </nav>
                    </div>
                  </div>
                  <div className='flex-shrink-0 w-14' aria-hidden='true'>
                    {/* <!-- Dummy element to force sidebar to shrink to fit close icon --> */}
                  </div>
                </Transition.Child>
              </div>
            </nav>
          )}
        </Transition>

        {/* <!-- Static sidebar for desktop --> */}
        <div className='hidden md:flex flex-shrink-0 h-full sticky top-0'>
          <div className='flex w-full flex-col h-screen overflow-scroll '>
            {/* <!-- Sidebar component, swap this element with another sidebar if you like --> */}
            <nav className='border-r border-gray-200 pt-5 pb-4 flex flex-col flex-grow '>
              <div className='flex-shrink-0 px-4 '>
                <Link href='/'>
                  <a className='flex items-center'>
                    <LogoIcon className='h-24 mx-auto my-8' />
                  </a>
                </Link>
              </div>
              <div className='flex-grow mt-5 flex flex-col'>
                <div className='flex-1 space-y-1'>
                  <AdminLink
                    href='/admin'
                    selected={
                      route.split("/")[1] === "admin" &&
                      route.split("/").length < 3
                    }>
                    <svg
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      aria-hidden='true'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
                      />
                    </svg>
                    Home
                  </AdminLink>

                  <AdminLink
                    href='/admin/posts'
                    selected={route.includes("/posts")}>
                    <svg
                      aria-hidden='true'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                      />
                    </svg>
                    Posts
                  </AdminLink>

                  <AdminLink
                    href='/admin/orders'
                    selected={route.includes("/orders")}>
                    <svg
                      aria-hidden='true'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z'
                      />
                    </svg>
                    Orders
                  </AdminLink>

                  <AdminLink
                    href='/admin/books'
                    selected={route.includes("/books")}>
                    <svg
                      aria-hidden='true'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                      />
                    </svg>
                    Books
                  </AdminLink>

                  <AdminLink
                    href='/admin/categories'
                    selected={route.includes("/categories")}>
                    <svg
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z'
                      />
                    </svg>
                    Categories
                  </AdminLink>

                  <AdminLink
                    href='/admin/tags'
                    selected={route.includes("/tags")}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z'
                      />
                    </svg>
                    Tags
                  </AdminLink>

                  <AdminLink
                    href='/admin/products'
                    selected={route.includes("/products")}>
                    <svg
                      aria-hidden='true'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z'
                      />
                    </svg>
                    Products
                  </AdminLink>

                  <AdminLink
                    href='/admin/subscriptions'
                    selected={route.includes("/subscriptions")}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7'
                      />
                    </svg>
                    Subscriptions
                  </AdminLink>

                  <AdminLink
                    href='/admin/vouchers'
                    selected={route.includes("/vouchers")}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z'
                      />
                    </svg>
                    Vouchers / Gift Cards
                  </AdminLink>

                  <AdminLink
                    href='/admin/discounts'
                    selected={route.includes("/discounts")}>
                    <svg
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z'
                      />
                    </svg>
                    Discounts
                  </AdminLink>

                  <AdminLink
                    href='/admin/events'
                    selected={route.includes("/events")}>
                    <svg
                      aria-hidden='true'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                      />
                    </svg>
                    Events & Festivals
                  </AdminLink>

                  <AdminLink
                    href='/admin/pages'
                    selected={route.includes("/pages")}>
                    <svg
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      aria-hidden='true'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2'
                      />
                    </svg>
                    Pages
                  </AdminLink>

                  <AdminLink
                    href='/admin/settings'
                    selected={route.includes("/settings")}>
                    <svg
                      aria-hidden='true'
                      className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
                      />
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                      />
                    </svg>
                    Settings
                  </AdminLink>
                </div>
              </div>
              <div className='flex-shrink-0 block w-full'>
                <Link href='/logout'>
                  <a className='group py-2 px-4 flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='text-gray-400 group-hover:text-gray-500 mr-3 h-6 w-6'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={2}>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
                      />
                    </svg>
                    Logout
                  </a>
                </Link>
              </div>
            </nav>
          </div>
        </div>

        {/* <!-- Content area --> */}
        <div className='flex-1 flex flex-col bg-gray-100'>
          <div className='w-full xl:px-0'>
            <div className='relative z-10 flex-shrink-0 h-16 bg-white border-b border-gray-200 flex'>
              <button
                onClick={() => setMenuOpen(true)}
                className='border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500 md:hidden'>
                <span className='sr-only'>Open sidebar</span>
                {/* <!-- Heroicon name: menu-alt-2 --> */}
                <svg
                  aria-hidden='true'
                  className='h-6 w-6'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M4 6h16M4 12h16M4 18h7'
                  />
                </svg>
              </button>
              {searchBar}
            </div>
          </div>
          <main className='p-4 md:p-8 flex flex-col bg-gray-50 h-full'>
            {children}
          </main>
        </div>
      </div>
    </>
  );
};
